import type { ActivityStatusBucket } from "@/modules/dashboard/payments-activity/types/paymentsActivity.types";

/**
 * Mirrors coloca-nest `LedgerOperationType` (`ledger/enums/ledger-operation-type.enum.ts`).
 *
 * `FEE` and `ADJUST` exist in the backend enum but **no service writes them today** — they are kept here so
 * an entry that starts using them still renders with a label instead of a raw enum value. `REVERSE` is NOT an
 * operation the user picks: it is the `operation_type` the ledger stamps on the mirror entry of a reversal
 * (see `LEDGER_REVERSAL_TYPE`).
 */
export const LedgerOperationType = {
  PAYIN: "PAYIN",
  PAYOUT: "PAYOUT",
  SWAP: "SWAP",
  FEE: "FEE",
  ADJUST: "ADJUST",
  REVERSE: "REVERSE",
} as const;
export type LedgerOperationType = (typeof LedgerOperationType)[keyof typeof LedgerOperationType];

/** Mirrors coloca-nest `LedgerDirection` — the wire values are single letters, not the enum member names. */
export const LedgerDirection = {
  DEBIT: "D",
  CREDIT: "C",
} as const;
export type LedgerDirection = (typeof LedgerDirection)[keyof typeof LedgerDirection];

/** Mirrors coloca-nest `LedgerAccountType`. Drives which direction increases the account's balance. */
export const LedgerAccountType = {
  ASSET: "ASSET",
  LIABILITY: "LIABILITY",
  REVENUE: "REVENUE",
  EXPENSE: "EXPENSE",
  SUSPENSE: "SUSPENSE",
} as const;
export type LedgerAccountType = (typeof LedgerAccountType)[keyof typeof LedgerAccountType];

/**
 * Mirrors coloca-nest `LedgerAccountEntity`, as returned by the `account` relation of a leg.
 *
 * `scope_provider_code` is provider identity — it is a wire field only and **must never be rendered**
 * (project rule 12). The account `code` itself is safe: it names the role ("CASH-AT-PROVIDER"), never the
 * provider.
 */
export interface LedgerAccount {
  id: string;
  /** Accounting code, e.g. `"2100-CLIENT-LIABILITY"`. Matched against the frontend catalog for its label. */
  code: string;
  /** Backend-side name, in English (`"Client liability"`). Only a fallback for an unknown code. */
  name: string;
  type: LedgerAccountType;
  /** Set on organization-scoped accounts (client liability) — resolved to a name via the org catalog. */
  scope_organization_id: string | null;
  /** ⚠️ Provider identity — never render (rule 12). Presence alone means "provider-scoped account". */
  scope_provider_code: string | null;
  currency: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

/**
 * One leg (renglón) of a double-entry journal entry, exactly as `GET /internal/payments/ledger/entries`
 * returns it. All money is a decimal string at 8 dp — format with Intl, never `Number` arithmetic.
 */
export interface LedgerLeg {
  id: string;
  /** Monotonic insertion sequence (bigint serialized as string) — the only strict chronological order. */
  seq: string;
  /** Groups every leg of ONE balanced entry. The backend guarantees debits = credits per currency inside it. */
  entry_id: string;
  operation_type: LedgerOperationType;
  operation_id: string;
  account_id: string;
  account: LedgerAccount;
  direction: LedgerDirection;
  /** Always POSITIVE — the sign is carried by `direction`, not by the amount. */
  amount: string;
  currency: string;
  /** Balance of THIS account after the leg posted (not the operation's balance). */
  balance_after: string;
  posted_at: string;
  /** Free-text, English, written by the runtime (`"payout reserve"`, or the reason on a reversal). */
  description: string | null;
  /** Set only on the mirror legs of a reversal — points at the reversed `entry_id`. */
  reverses_entry_id: string | null;
  created_at: string;
}

/** Debit/credit totals for one currency. The backend invariant is `debit === credit`, per currency. */
export interface LedgerCurrencyTotal {
  currency: string;
  debit: string;
  credit: string;
  isBalanced: boolean;
}

/** A journal entry (asiento): the legs sharing one `entry_id`, plus everything derived from them. */
export interface LedgerEntryGroup {
  entryId: string;
  /** 1-based chronological position within the operation — what the UI shows as "Asiento #n". */
  index: number;
  operationType: LedgerOperationType;
  postedAt: string;
  /** Shared by every leg of the entry (the backend writes one description per entry). */
  description: string | null;
  /** Set when this entry IS a reversal — the `entry_id` it mirrors. */
  reversesEntryId: string | null;
  /** Index of the entry it reverses, when that entry is part of the loaded trail (it always should be). */
  reversesEntryIndex: number | null;
  /** Indexes of the entries that reverse THIS one (usually 0 or 1). */
  reversedByIndexes: number[];
  legs: LedgerLeg[];
  totals: LedgerCurrencyTotal[];
  isBalanced: boolean;
}

/** Net effect of the whole operation on one (account, currency) pair — the accountant's summary. */
export interface LedgerAccountRollupRow {
  key: string;
  account: LedgerAccount;
  currency: string;
  debit: string;
  credit: string;
  /** `debit - credit`, signed. Its sign names the side the account closes on for this operation. */
  net: string;
  /** Which side the net falls on, or null when the operation left the account flat. */
  netSide: LedgerDirection | null;
  /** `balance_after` of the account's LAST leg in this operation. */
  lastBalanceAfter: string;
  legCount: number;
}

/** Everything the ledger pane renders, derived from the raw legs of one operation. */
export interface LedgerOperationView {
  /** Raw legs, merged across the operation's own type + `REVERSE`, ordered by `seq` ASC. */
  legs: LedgerLeg[];
  entries: LedgerEntryGroup[];
  /** Debit/credit totals per currency across the whole operation. */
  totals: LedgerCurrencyTotal[];
  rollup: LedgerAccountRollupRow[];
  /** Entries whose `reverses_entry_id` is set. */
  reversalCount: number;
  /** True when every currency of every entry balances. False is a red flag, never a rounding artifact. */
  isBalanced: boolean;
  /** Organizations touched, read from the account scopes (a payout touches exactly one). */
  organizationIds: string[];
  currencies: string[];
  firstPostedAt: string | null;
  lastPostedAt: string | null;
}

/**
 * Which runtime list endpoint the operation explorer is reading. These are three distinct endpoints with
 * three distinct status vocabularies — never share a status map between them.
 */
export type LedgerOperationKind = "payouts" | "payins" | "swaps";

/** A row of the operation explorer — normalized across payouts, payins and swaps. */
export interface LedgerOperationCandidate {
  kind: LedgerOperationKind;
  /** The `operation_type` to send to the ledger endpoint for this candidate. */
  ledgerType: Extract<LedgerOperationType, "PAYIN" | "PAYOUT" | "SWAP">;
  id: string;
  organizationId: string;
  /** Raw backend status of its own vocabulary. */
  status: string;
  bucket: ActivityStatusBucket;
  /** True while the runtime can still post new entries for it — the only reason this view polls. */
  isNonTerminal: boolean;
  /** Headline amount, decimal string. Payouts/payins: net; swaps: amount received. */
  amount: string;
  currency: string;
  /** Corridor (`MEX · SPEI`) for payouts/payins, currency pair (`USD → COP`) for swaps. */
  subtitle: string;
  createdAt: string;
}

/** Server-side filters of the explorer — only what the three list endpoints actually support. */
export interface LedgerPickerFilters {
  /** INTERNAL staff browse cross-org by default; this narrows to one client organization. */
  organizationId: string | null;
  /** Raw status enum value of the ACTIVE kind's vocabulary. */
  status: string | null;
  /** ISO 3166-1 alpha-3. Not applicable to swaps (they have no corridor). */
  country: string | null;
  /** Rolling `created_at` window in days. */
  rangeDays: number | null;
}
